import axios from 'axios';
import type { SearchSuggestion } from '../../favorites-app/types';
import { notifyFavoritesChanged } from '~/ui-apps/hooks/useFavoritesCount';

interface FavoritesResponse {
  success?: boolean;
  data?: unknown[];
}

function toPositiveInt(value: unknown): number | null {
  const id = typeof value === 'number' ? value : Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
}

function extractExistingTickerIds(rows: unknown[]): Set<number> {
  const ids = new Set<number>();
  for (const row of rows) {
    if (!row || typeof row !== 'object') {
      continue;
    }
    const tickerId = toPositiveInt((row as Record<string, unknown>).ticker_id);
    if (tickerId != null) {
      ids.add(tickerId);
    }
  }
  return ids;
}

/**
 * Reconciles onboarding company selections with saved favorites:
 * adds newly selected tickers and removes deselected ones.
 */
export async function syncOnboardingFavorites(
  userId: number,
  selectedCompanies: SearchSuggestion[],
): Promise<void> {
  const selectedTickerIds = selectedCompanies
    .map((company) => toPositiveInt(company.ticker_id))
    .filter((id): id is number => id != null);

  if (selectedCompanies.length > 0 && selectedTickerIds.length !== selectedCompanies.length) {
    throw new Error('Unable to save companies: missing ticker information');
  }

  const selectedIdSet = new Set(selectedTickerIds);

  const response = await axios.get<FavoritesResponse>(`/api/users/favorites/${userId}`);
  const existingRows =
    response.data.success && Array.isArray(response.data.data) ? response.data.data : [];
  const existingIds = extractExistingTickerIds(existingRows);

  const toAdd = selectedTickerIds.filter((id) => !existingIds.has(id));
  const toRemove = [...existingIds].filter((id) => !selectedIdSet.has(id));

  if (toAdd.length === 0 && toRemove.length === 0) {
    return;
  }

  await Promise.all([
    toAdd.length > 0
      ? axios.post('/api/users/add-following-tickers-batch', { ticker_ids: toAdd })
      : Promise.resolve(),
    ...toRemove.map((tickerId) =>
      axios.post('/api/users/remove-following-ticker', {
        ticker_id: tickerId,
        user_id: userId,
      }),
    ),
  ]);

  notifyFavoritesChanged();
}
